import { ref, onMounted, onUnmounted } from 'vue'
import { createLogger } from '@/utils/logger'

const logger = createLogger('usePWA')

export interface PWAInstallPrompt extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function usePWA() {
  const isInstalled = ref(false)
  const isInstallable = ref(false)
  const deferredPrompt = ref<PWAInstallPrompt | null>(null)
  const isIOS = ref(false)
  const isStandalone = ref(false)
  const isOnline = ref(navigator.onLine)

  const checkInstallStatus = () => {
    // Check for dev mode override ONLY in development environment
    const isDevelopment =
      import.meta.env.DEV || import.meta.env.MODE === 'development'

    if (isDevelopment) {
      const devOverride = localStorage.getItem('dev-pwa-installed')
      if (devOverride === 'true') {
        logger.log('DEV MODE: PWA installed override enabled')
        isInstalled.value = true
        isStandalone.value = true
        return
      } else if (devOverride === 'false') {
        logger.log('DEV MODE: PWA installed override disabled')
        isInstalled.value = false
        isStandalone.value = false
        return
      }
    }

    // Production logic: Check if running in standalone mode (installed PWA)
    isStandalone.value =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true

    // Check if it's iOS
    isIOS.value = /iPad|iPhone|iPod/.test(navigator.userAgent)

    // On iOS, check if already added to home screen
    if (isIOS.value) {
      isInstalled.value = isStandalone.value
    } else {
      // For other browsers, check if running in standalone mode
      isInstalled.value = isStandalone.value
    }

    logger.log('PWA install status checked:', {
      isInstalled: isInstalled.value,
      isStandalone: isStandalone.value,
      isIOS: isIOS.value,
      isDevelopment,
    })
  }

  const handleBeforeInstallPrompt = (e: Event) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault()
    // Stash the event so it can be triggered later
    deferredPrompt.value = e as PWAInstallPrompt
    isInstallable.value = true
  }

  const handleAppInstalled = () => {
    isInstalled.value = true
    isInstallable.value = false
    deferredPrompt.value = null
  }

  const installPWA = async (): Promise<boolean> => {
    if (!deferredPrompt.value) {
      return false
    }

    try {
      // Show the install prompt
      await deferredPrompt.value.prompt()

      // Wait for the user to respond to the prompt
      const choiceResult = await deferredPrompt.value.userChoice

      if (choiceResult.outcome === 'accepted') {
        isInstalled.value = true
        isInstallable.value = false
      }

      deferredPrompt.value = null
      return choiceResult.outcome === 'accepted'
    } catch (error) {
      logger.error('Error installing PWA:', error)
      return false
    }
  }

  const getInstallInstructions = (): string => {
    if (isIOS.value) {
      return 'To install this app on your iOS device, tap the share button and then "Add to Home Screen".'
    } else {
      return 'To install this app, look for the install button in your browser or use the browser menu.'
    }
  }

  const canInstall = (): boolean => {
    return !isInstalled.value && (isInstallable.value || isIOS.value)
  }

  const isPWACapable = (): boolean => {
    // Check if the browser supports PWAs
    return 'serviceWorker' in navigator && 'PushManager' in window
  }

  // Event handlers hoisted for cleanup
  const handleOnline = () => {
    isOnline.value = true
  }
  const handleOffline = () => {
    isOnline.value = false
  }
  const handlePWAModeChange = () => {
    checkInstallStatus()
  }
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === 'dev-pwa-installed') {
      checkInstallStatus()
    }
  }
  const mediaQuery = window.matchMedia('(display-mode: standalone)')
  const handleDisplayModeChange = () => {
    isStandalone.value = mediaQuery.matches
    isInstalled.value = mediaQuery.matches
  }

  onMounted(() => {
    // Clean up dev overrides in production builds
    const isDevelopment =
      import.meta.env.DEV || import.meta.env.MODE === 'development'
    if (!isDevelopment) {
      const devOverride = localStorage.getItem('dev-pwa-installed')
      if (devOverride !== null) {
        logger.log('PRODUCTION: Removing dev-pwa-installed override')
        localStorage.removeItem('dev-pwa-installed')
      }
    }

    checkInstallStatus()

    // Listen for online/offline events
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Listen for the appinstalled event
    window.addEventListener('appinstalled', handleAppInstalled)

    // Listen for PWA mode changes (dev mode toggle)
    window.addEventListener('pwa-mode-change', handlePWAModeChange)

    // Listen for storage changes (cross-tab dev mode changes)
    window.addEventListener('storage', handleStorageChange)

    // Check for display mode changes
    mediaQuery.addEventListener('change', handleDisplayModeChange)
  })

  onUnmounted(() => {
    // Remove all event listeners to prevent memory leaks
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleAppInstalled)
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    window.removeEventListener('pwa-mode-change', handlePWAModeChange)
    window.removeEventListener('storage', handleStorageChange)
    mediaQuery.removeEventListener('change', handleDisplayModeChange)
  })

  return {
    isInstalled,
    isInstallable,
    isIOS,
    isStandalone,
    isOnline,
    installPWA,
    getInstallInstructions,
    canInstall,
    isPWACapable,
  }
}
